import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Bookmark } from "lucide-react";

interface BookmarkButtonProps {
  newsId: number;
  variant?: "default" | "ghost" | "outline";
  size?: "default" | "sm" | "lg" | "icon";
  showLabel?: boolean;
  className?: string;
}

export function BookmarkButton({
  newsId,
  variant = "ghost",
  size = "sm",
  showLabel = false,
  className,
}: BookmarkButtonProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Check if bookmarked (also returns count)
  const { data: bookmarkStatus, isLoading } = useQuery<{ isBookmarked: boolean; bookmarkCount: number }>({
    queryKey: [`/api/news/${newsId}/bookmark`],
    enabled: !!user,
    staleTime: 60000,
  });

  const isBookmarked = bookmarkStatus?.isBookmarked || false;
  const bookmarkCount = bookmarkStatus?.bookmarkCount || 0;

  // Add bookmark mutation with optimistic update
  const addBookmarkMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", `/api/news/${newsId}/bookmark`);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [`/api/news/${newsId}/bookmark`] });
      const previous = queryClient.getQueryData<{ isBookmarked: boolean }>([`/api/news/${newsId}/bookmark`]);

      queryClient.setQueryData([`/api/news/${newsId}/bookmark`], { isBookmarked: true });

      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/news/bookmarks"] });
      toast({
        title: "Saved to bookmarks",
        description: "You can find this in your saved items.",
      });
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData([`/api/news/${newsId}/bookmark`], context.previous);
      }
      toast({
        title: "Error",
        description: "Could not save bookmark. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/news/${newsId}/bookmark`] });
    },
  });

  // Remove bookmark mutation with optimistic update
  const removeBookmarkMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/news/${newsId}/bookmark`);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [`/api/news/${newsId}/bookmark`] });
      const previous = queryClient.getQueryData<{ isBookmarked: boolean }>([`/api/news/${newsId}/bookmark`]);

      queryClient.setQueryData([`/api/news/${newsId}/bookmark`], { isBookmarked: false });

      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/news/bookmarks"] });
      toast({
        title: "Removed from bookmarks",
        description: "This item has been removed from your saved items.",
      });
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData([`/api/news/${newsId}/bookmark`], context.previous);
      }
      toast({
        title: "Error",
        description: "Could not remove bookmark. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/news/${newsId}/bookmark`] });
    },
  });

  const handleToggleBookmark = () => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to bookmark items.",
      });
      return;
    }

    if (isBookmarked) {
      removeBookmarkMutation.mutate();
    } else {
      addBookmarkMutation.mutate();
    }
  };

  const isPending = addBookmarkMutation.isPending || removeBookmarkMutation.isPending;

  if (size === "icon") {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={variant}
              size="icon"
              onClick={handleToggleBookmark}
              disabled={isPending || isLoading}
              className={cn(
                isBookmarked && "text-yellow-500 hover:text-yellow-600",
                className
              )}
            >
              <Bookmark
                className="h-4 w-4"
                fill={isBookmarked ? "currentColor" : "none"}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {isBookmarked ? "Remove from saved" : "Save for later"}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleToggleBookmark}
      disabled={isPending || isLoading}
      className={cn(
        "gap-2",
        isBookmarked && "text-yellow-500 hover:text-yellow-600",
        className
      )}
    >
      <Bookmark
        className="h-4 w-4"
        fill={isBookmarked ? "currentColor" : "none"}
      />
      {showLabel && (
        <span>{isBookmarked ? "Saved" : "Save"}{bookmarkCount > 0 ? ` (${bookmarkCount})` : ""}</span>
      )}
    </Button>
  );
}
